import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import { registerPaidyTokenApi } from '@/api/payment';
import {
  getSubscriptionStatusApi,
  postSubscriptionChangePaymentApi,
  postSubscriptionCancelApi,
} from '@/api/subscription';
import { RoundSheet } from '@/components/uiParts/Sheet/RoundSheet/RoundSheet';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { paymentTypeId } from '@/consts/payment';
import { useGmo } from '@/hooks/useGmo';
import { usePaidy } from '@/hooks/usePaidy';
import { SubscriptionStatus } from '@/types/api/subscription';
import { PAIDY_LOGO_URL } from '@/utils/payment';
import { clearSubscriptionPaymentAffiliate } from '@/utils/subscriptionPaymentAffiliate';

export const SubscriptionMyPage: FC = () => {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaymentChange, setShowPaymentChange] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const authInfo = store.state.authInfo as any;
  const selectedCar = useStore(store, 'carItemInfo') as any;
  const paymentMethods = useStore(store, 'getPaymentMethodsInfo') as any[];
  const { launchPaidyCheckout } = usePaidy();
  const { addCreditCardPaymentMethod } = useGmo(paths.subscriptionMyPage);

  const getPaymentType = (pm: any) =>
    Number(pm.paymentTypeId || pm.m_payment_type_id || 0);
  const registeredCards = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.CREDIT,
  );
  const registeredPaidys = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.PAIDY,
  );

  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelPanel, setShowCancelPanel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const cancelReasons = [
    'サービスを利用する機会がなかった',
    '月額料金が高いと感じた',
    '車を手放した・乗り換えた',
    '他のサービスに切り替えた',
    '対象外の修理が多かった',
    'その他',
  ];

  const subscriptionId = subscriptionStatus?.t_subscription_id;

  const handleCancelSubmit = () => {
    if (!cancelReason) {
      f7.dialog.alert('解約理由を選択してください');
      return;
    }
    f7.dialog.confirm(
      '今月末での解約となります。\nよろしいですか？',
      '解約の確認',
      async () => {
        if (!subscriptionId) return;
        setIsCancelling(true);
        try {
          const res = await postSubscriptionCancelApi({
            t_subscription_id: subscriptionId,
            cancel_reason: cancelReason,
          });
          if (res.data.success) {
            clearSubscriptionPaymentAffiliate(
              authInfo.m_customer_id,
              subscriptionId,
            );
            f7.dialog.alert(
              res.data.data?.message || '解約申込を受け付けました。',
              '',
              () => refreshStatus(),
            );
          } else {
            f7.dialog.alert(res.data.error_message || '解約申込に失敗しました');
          }
        } catch (e: any) {
          f7.dialog.alert(e.message || 'エラーが発生しました');
        } finally {
          setIsCancelling(false);
        }
      },
    );
  };

  const handleChangeToCard = async (card: any) => {
    if (isChanging) return;
    if (!subscriptionId) return;
    setIsChanging(true);
    try {
      const methodId = card.m_payment_method_id || card.paymentMethodId;
      const res = await postSubscriptionChangePaymentApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.CREDIT,
        m_payment_method_id: methodId,
      });
      if (res.data.success) {
        f7.dialog.alert('お支払い方法を変更しました', '', () => {
          setShowPaymentChange(false);
          refreshStatus();
        });
      }
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsChanging(false);
    }
  };

  const handleChangeToPaidy = async () => {
    if (isChanging) return;
    if (!subscriptionId) return;
    setIsChanging(true);
    try {
      // 既にPaidyトークンがあればそれを使う
      if (registeredPaidys.length > 0) {
        const pm = registeredPaidys[registeredPaidys.length - 1];
        const methodId = pm.m_payment_method_id || pm.paymentMethodId;
        const res = await postSubscriptionChangePaymentApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: methodId,
        });
        if (res.data.success) {
          f7.dialog.alert('お支払い方法を変更しました', '', () => {
            setShowPaymentChange(false);
            refreshStatus();
          });
        }
      } else {
        // 新規トークン登録
        const tokenResult = await launchPaidyCheckout({ mode: 'token' });
        if (!tokenResult?.tokenId) {
          setIsChanging(false);
          return;
        }
        const registerRes = await registerPaidyTokenApi({
          m_customer_id: authInfo.m_customer_id,
          paidy_token_id: tokenResult.tokenId,
          customer_name: '',
          customer_email: '',
          phone_number: '',
        });
        if (!registerRes.data.success)
          throw new Error('トークン登録に失敗しました');
        const paidyMethodId = (registerRes.data as any).data?.id;
        const res = await postSubscriptionChangePaymentApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: paidyMethodId,
        });
        if (res.data.success) {
          f7.dialog.alert('お支払い方法を変更しました', '', () => {
            setShowPaymentChange(false);
            refreshStatus();
          });
        }
      }
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsChanging(false);
    }
  };

  const refreshStatus = async () => {
    try {
      // 選択中の車両で取得を試行
      const res = await getSubscriptionStatusApi(
        authInfo.m_customer_id,
        selectedCar?.t_stock_car_id,
      );
      if (res.data.success && res.data.data) {
        const sub =
          res.data.data.subscription ?? res.data.data.pending_subscription;
        if (sub) {
          setSubscriptionStatus(sub);
          return;
        }
      }
      // 車両IDで見つからない場合、車両IDなしで再取得（t_stock_car_idがNULLのケース）
      const fallbackRes = await getSubscriptionStatusApi(
        authInfo.m_customer_id,
      );
      if (fallbackRes.data.success && fallbackRes.data.data) {
        const sub =
          fallbackRes.data.data.subscription ??
          fallbackRes.data.data.pending_subscription;
        if (sub) {
          setSubscriptionStatus(sub);
        }
      }
    } catch (e) {
      console.error('契約状態取得エラー', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authInfo?.m_customer_id) {
      refreshStatus();
    }
  }, [authInfo?.m_customer_id, selectedCar?.t_stock_car_id]);

  const handleClickCoverage = (coverageName: string) => {
    // 商材に応じて既存画面に遷移
    if (coverageName.includes('保証')) {
      f7.view.main.router.navigate(paths.warranty('false'));
    } else if (coverageName.includes('資産価値')) {
      f7.view.main.router.navigate(paths.carAssetValue);
    } else if (coverageName.includes('クーポン')) {
      f7.view.main.router.navigate(paths.coupons);
    }
  };

  const handleClickTerm = (termType: number) => {
    f7.view.main.router.navigate(paths.terms, {
      props: { termType },
    });
  };

  if (isLoading) {
    return (
      <PageTemplate
        pageName="subscription-mypage"
        title="サブスクリプション契約"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          読み込み中...
        </div>
      </PageTemplate>
    );
  }

  if (!subscriptionStatus) {
    return (
      <PageTemplate
        pageName="subscription-mypage"
        title="サブスクリプション契約"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          契約情報が見つかりません
        </div>
      </PageTemplate>
    );
  }

  const statusLabel: Record<number, string> = {
    1: '有効',
    2: '停止中',
    3: '解約済',
  };

  return (
    <PageTemplate
      pageName="subscription-mypage"
      title="サブスクリプション契約"
      showNavBack
    >
      <div style={{ padding: '16px' }}>
        {/* 契約情報 */}
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {subscriptionStatus.plan_name}
            </span>
            <span
              style={{
                fontSize: '12px',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor:
                  subscriptionStatus.status === 1 ? '#E8F5E9' : '#FFF3E0',
                color: subscriptionStatus.status === 1 ? '#2E7D32' : '#E65100',
              }}
            >
              {statusLabel[subscriptionStatus.status] || '不明'}
            </span>
          </div>

          <div style={{ fontSize: '14px', color: '#666', lineHeight: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>契約番号</span>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                {subscriptionStatus.subscription_no}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>月額料金</span>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                ¥{subscriptionStatus.monthly_billing_amount.toLocaleString()}/月
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>継続月数</span>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                {subscriptionStatus.continuous_months}ヶ月
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>契約開始日</span>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                {subscriptionStatus.start_date
                  ? new Date(subscriptionStatus.start_date).toLocaleDateString(
                      'ja-JP',
                    )
                  : '-'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>お支払い方法</span>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                {(() => {
                  const pt =
                    (subscriptionStatus as any).payment_type_id ||
                    (subscriptionStatus as any).m_payment_type_id;
                  if (pt === 9 || pt === '9') return 'ペイディ';
                  if (pt === 3 || pt === '3') return 'クレジットカード';
                  return '未設定';
                })()}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowPaymentChange(!showPaymentChange)}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px',
              fontSize: '13px',
              color: '#1E55E6',
              backgroundColor: '#fff',
              border: '1px solid #1E55E6',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            お支払い方法を変更する
          </button>
        </div>

        {/* 決済方法変更パネル */}
        <button
          onClick={() =>
            f7.view.main.router.navigate(paths.subscriptionCarSelect)
          }
          style={{
            width: '100%',
            marginBottom: '16px',
            padding: '12px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#1E55E6',
            backgroundColor: '#fff',
            border: '1px solid #1E55E6',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          別の車両で申し込む
        </button>

        {showPaymentChange && (
          <div
            style={{
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '12px',
              }}
            >
              お支払い方法を選択
            </div>

            {registeredCards.map((card: any) => (
              <div
                key={card.m_payment_method_id || card.paymentMethodId}
                onClick={() => handleChangeToCard(card)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  cursor: isChanging ? 'wait' : 'pointer',
                  opacity: isChanging ? 0.6 : 1,
                }}
              >
                <i
                  className="icon-credit-card"
                  style={{
                    fontSize: '18px',
                    marginRight: '10px',
                    color: '#333',
                  }}
                />
                <div style={{ flex: 1, fontSize: '14px' }}>
                  クレジットカード（{card.cardNo || card.card_no}）
                </div>
              </div>
            ))}

            <div
              onClick={handleChangeToPaidy}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                marginBottom: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                cursor: isChanging ? 'wait' : 'pointer',
                opacity: isChanging ? 0.6 : 1,
              }}
            >
              <img
                src={PAIDY_LOGO_URL}
                alt="ペイディ"
                style={{
                  width: '50px',
                  height: 'auto',
                  objectFit: 'contain',
                  marginRight: '10px',
                }}
              />
              <div style={{ flex: 1, fontSize: '14px' }}>
                あと払い（ペイディ）
              </div>
            </div>

            <button
              onClick={() => setShowPaymentChange(false)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '13px',
                color: '#666',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              キャンセル
            </button>
          </div>
        )}

        {/* 商材一覧 */}
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
          }}
        >
          ご利用中のサービス
        </h3>
        <RoundSheet>
          {subscriptionStatus.coverages
            .filter((c) => c.icon !== 'ticket')
            .map((coverage, i) => (
              <div
                key={coverage.id}
                style={{
                  padding: '14px 0',
                  borderTop: i > 0 ? '1px solid #efefef' : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    {coverage.coverage_name}
                  </div>
                </div>
                {coverage.coverage_description && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    {coverage.coverage_description}
                  </div>
                )}
                {coverage.term_type && (
                  <div
                    onClick={() => handleClickTerm(coverage.term_type!)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: '8px',
                      padding: '10px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#1E55E6' }}>
                      {coverage.coverage_name}利用規約
                    </span>
                    <i
                      className="icon-right"
                      style={{ fontSize: '12px', color: '#bbb' }}
                    />
                  </div>
                )}
              </div>
            ))}
        </RoundSheet>

        {/* サブスクサービス規約 */}
        {(subscriptionStatus as any).plan_term_type && (
          <div
            onClick={() =>
              handleClickTerm((subscriptionStatus as any).plan_term_type)
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '12px',
              padding: '14px 16px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '14px', color: '#1E55E6' }}>
              サブスクリプションサービス利用規約
            </span>
            <i
              className="icon-right"
              style={{ fontSize: '12px', color: '#bbb' }}
            />
          </div>
        )}
        <div style={{ marginBottom: '16px' }} />

        {/* 解約 */}
        {subscriptionStatus.status === 1 && (
          <div
            style={{
              paddingTop: '24px',
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
            }}
          >
            {subscriptionStatus.apply_status === 3 ? (
              <div
                style={{
                  fontSize: '13px',
                  color: '#E65100',
                  padding: '12px',
                  backgroundColor: '#FFF3E0',
                  borderRadius: '8px',
                }}
              >
                解約予定（今月末で解約されます）
              </div>
            ) : !showCancelPanel ? (
              <button
                onClick={() => {
                  setShowCancelPanel(true);
                  setTimeout(() => {
                    document
                      .getElementById('cancel-panel')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                style={{
                  fontSize: '13px',
                  color: '#999',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  textDecoration: 'underline',
                }}
              >
                解約する
              </button>
            ) : (
              <div
                id="cancel-panel"
                style={{
                  textAlign: 'left',
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  解約理由をお聞かせください
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '16px',
                  }}
                >
                  サービス改善のために、差し支えなければ理由をお選びください。
                </div>
                {cancelReasons.map((reason) => (
                  <div
                    key={reason}
                    onClick={() => setCancelReason(reason)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border:
                          cancelReason === reason
                            ? '6px solid #1E55E6'
                            : '2px solid #ccc',
                        flexShrink: 0,
                        boxSizing: 'border-box',
                      }}
                    />
                    <span>{reason}</span>
                  </div>
                ))}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '16px',
                  }}
                >
                  <button
                    onClick={() => {
                      setShowCancelPanel(false);
                      setCancelReason('');
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      fontSize: '13px',
                      color: '#666',
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleCancelSubmit}
                    disabled={!cancelReason || isCancelling}
                    style={{
                      flex: 1,
                      padding: '12px',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      color: '#fff',
                      backgroundColor:
                        cancelReason && !isCancelling ? '#C62828' : '#ccc',
                      border: 'none',
                      borderRadius: '8px',
                      cursor:
                        cancelReason && !isCancelling
                          ? 'pointer'
                          : 'not-allowed',
                    }}
                  >
                    {isCancelling ? '処理中...' : '解約する'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageTemplate>
  );
};
